import {
  Flex,
  GButton,
  IconLocation,
  IconTruck,
  Link,
  Text,
} from '@gazin/ecommerce-ui'
import dynamic from 'next/dynamic'
import {
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import InputMask from 'react-input-mask'
import { toast } from 'react-toastify'
import {
  FreightOption,
  FreightResponse,
} from 'src/services/marketplace-api/dtos'
import { getReverseGeocode } from 'src/services/shipping'
import { theme } from 'src/styles/themes/default'
import { formatarCep, mascaraCep } from 'src/util/formatar'
import { getStyles } from './styles'
import { SkeletonSearchShipping } from './searchShipping.skeleton'
import { simularFrete } from 'src/services/frete'
import useZipcode from 'src/contexto/hooks/useZipcode'
import { BadgeMontagemGratis } from 'src/components/badgeMontagemGratis'
import { addressFormatted } from 'src/util/address'
import { sortStoresByDeliveryTimeInHours } from 'src/util/products'

const DeliveryType = dynamic(() => import('./deliveryType'), {
  ssr: false,
})

type SearchShippingProps = {
  adId: number
  amount?: number
  label: string
  loading: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
  setShipping?: Dispatch<SetStateAction<FreightResponse[] | undefined>>
  variationId: number
}

export const SearchShipping = forwardRef<any, SearchShippingProps>(
  (
    { adId, label, loading, variationId, amount = 1, setShipping, setLoading },
    ref
  ) => {
    const zipcodeContext = useZipcode()

    const [calculations, setCalculations] = useState<FreightResponse[]>([])
    const [errorMessage, setErrorMessage] = useState<string>()
    const [optionsShipping, setOptionsShipping] = useState<FreightOption[]>([])
    const [previousAd, setPreviousAd] = useState<number>()
    const [storeWithPickUp, setStoreWithPickUp] = useState([])
    const [zipCode, setZipCode] = useState(zipcodeContext.zipcode ?? '')
    const [address, setAddress] = useState('')

    const styles = getStyles(!!errorMessage)

    const hasFreeAssembly = useMemo(
      () =>
        optionsShipping?.some(
          (opcaoFrete) => opcaoFrete.montagem_gratis?.length
        ),
      [optionsShipping]
    )

    const isValidCep = useMemo(
      () => formatarCep(zipCode)?.length === 8,
      [zipCode]
    )

    useImperativeHandle(ref, () => ({ setError: setErrorMessage }), [])

    useEffect(() => {
      validateInformation()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adId])

    useEffect(() => {
      isValidCep && calculateShipping()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValidCep, amount, zipCode])

    useEffect(() => {
      if (!zipcodeContext.zipcode) return setZipCode('')

      if (zipcodeContext.zipcode !== zipCode) {
        setZipCode(zipcodeContext.zipcode)
      }
    }, [zipcodeContext.zipcode])

    useEffect(() => {
      setShipping?.(calculations)

      calculations?.forEach((calculo) => {
        if (adId == calculo.id_anuncio || calculations?.length === 1) {
          setCurrentShipping(calculo.mensagemErro, calculo.opcoes)
        }
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calculations, adId])

    useEffect(() => {
      const pickUp = calculations?.find(
        (calculation) => !!calculation.lojas_retirada?.length
      )

      if (!pickUp) return setStoreWithPickUp([])

      setStoreWithPickUp(
        pickUp.lojas_retirada.sort(sortStoresByDeliveryTimeInHours)
      )
    }, [adId, calculations])

    function validateInformation() {
      if (!calculations?.length || adId === previousAd) {
        return
      }

      setPreviousAd(adId)
      const calculation = calculations.find((item) => item.id_anuncio == adId)

      if (!calculation) {
        return calculateShipping()
      }

      if (calculation?.opcoes) {
        return setCurrentShipping(undefined, calculation.opcoes)
      }

      setCurrentShipping('Produto indisponível para sua região!')
    }

    function setCurrentShipping(
      mensagemErro?: string,
      opcoesFrete?: FreightOption[]
    ) {
      setErrorMessage(mensagemErro)
      setOptionsShipping(opcoesFrete || [])
    }

    function pressEnter(e: KeyboardEvent<HTMLInputElement>) {
      if (e.key === 'Enter') return calculateShipping()
    }

    async function calculateShipping() {
      storeWithPickUp?.length && setStoreWithPickUp([])

      setAddress('')
      setCurrentShipping()

      if (isValidCep) {
        setLoading(true)

        const { data, status } = await simularFrete(
          zipCode,
          variationId,
          amount
        )

        if (data.cep_info) {
          const address = addressFormatted(data.cep_info)
          setAddress(address)
        }

        const calculationsReturn = data?.cotacoes.map((data) => ({
          id_anuncio: data.anuncio_id,
          erro: !!data.mensagem_erro,
          mensagemErro: data.mensagem_erro,
          opcoes: data.opcoes?.map((opcao) => ({
            entrega: opcao.entrega,
            entrega_chave: opcao.entrega_chave,
            montagem_gratis: opcao?.montagem_gratis,
            prazo_dias: opcao.prazo_dias,
            prazo_estimado: opcao.prazo_estimado,
            prazo_estimado_dia_semana: opcao.prazo_estimado_dia_semana,
            prazo_horas: opcao.prazo_horas ?? null,
            preco: opcao.preco,
          })),
          lojas_retirada: data.lojas_retirada,
        }))

        switch (status) {
          case 500:
          case 400:
            setCurrentShipping('Ocorreu um erro ao calcular o frete.')
            setLoading(false)
            setCalculations([])
            setAddress('')
            return
          case 422:
            setCurrentShipping('Cep inválido')
            setLoading(false)
            setCalculations([])
            setAddress('')
            return
        }
        zipcodeContext.set(zipCode)
        setCalculations(calculationsReturn as FreightResponse[])
        setLoading(false)
        return
      }
      if (!formatarCep(zipCode)?.length) {
        zipcodeContext.remove()
      }
    }

    const enableLocation = (position: any) => {
      const latitude = position.coords.latitude
      const longitude = position.coords.longitude

      const at = `${latitude},${longitude}`

      getReverseGeocode(at)
        .then(({ data }) => {
          const zipcode = data.items[0].address.postalCode
          zipcodeContext.set(mascaraCep(zipcode))
        })
        .catch(() =>
          toast.error(
            'Não foi possível pegar sua localização neste momento, por favor verifique sua rede/gps e tente novamente.'
          )
        )
    }

    const handleLocation = () => {
      if (navigator.geolocation)
        return navigator.geolocation.getCurrentPosition(enableLocation)

      return toast.warn('Localização não suportada pelo navegador.')
    }

    return (
      <Flex flexDir="column">
        <Flex alignItems="center" flexWrap="wrap">
          <Flex
            alignItems="center"
            color={theme.colors.grey[300]}
            flexDir="row"
            fontSize="0.875rem"
            justifyContent="space-between"
            mb="1rem"
            mt="1.5rem"
            w="100%"
          >
            <Flex>
              <IconTruck h="1rem" mr="0.5rem" w="1.2rem" />
              <Text fontSize="0.875rem" lineHeight="1rem" mb="0">
                {label}
              </Text>
            </Flex>
            <Flex
              alignContent="center"
              color={theme.primary.main}
              justifyContent="center"
              lineHeight="0.875rem"
              transition="all 0.3s"
            >
              <IconLocation color="#0D71F0" height="1rem" width="1rem" />
              <Link
                data-testid="use-my-location"
                fontSize="0.75rem"
                marginBottom="0"
                onClick={handleLocation}
                rel="noopener noreferrer"
                target="_blank"
                textDecorationLine="underline"
              >
                Ativar localização
              </Link>
            </Flex>
          </Flex>
          <Flex
            alignItems="center"
            borderRadius="0.375rem"
            flex="1"
            justifyContent="space-between"
            gap={{ base: '0.5rem', md: '1rem' }}
            flexDirection={{ base: 'column', sm: 'row' }}
            w="100%"
          >
            <Flex flex="1" w="100%">
              <InputMask
                mask="99999-999"
                onChange={(cep) => setZipCode(cep.target.value)}
                onKeyPress={(e) => pressEnter(e)}
                placeholder="Digite seu CEP"
                style={{ ...styles }}
                type="text"
                value={zipCode}
              />
            </Flex>

            <GButton
              height="3rem"
              label="Ok"
              onClick={calculateShipping}
              minW={{ base: '100%', sm: 'auto' }}
              w={{ base: '100%', sm: 'auto' }}
            />
          </Flex>
          {!!isValidCep && (
            <Text
              mt={{ base: '0.5rem', md: '1rem' }}
              mb={0}
              fontWeight={500}
              fontSize="0.75rem"
              lineHeight="1.05rem"
              color="neutral.700"
            >
              {address}
            </Text>
          )}

          {errorMessage && (
            <Flex flexDirection="column">
              <Text color="#bc3838" fontSize="0.813rem" mt="0.375rem" mb="0">
                {errorMessage}
              </Text>
            </Flex>
          )}

          {hasFreeAssembly && (
            <Flex mt="0.5rem" w="100%">
              <BadgeMontagemGratis />
            </Flex>
          )}
        </Flex>

        {!errorMessage &&
          isValidCep &&
          (optionsShipping?.length > 0 ||
            storeWithPickUp?.length > 0 ||
            loading) &&
          (!loading ? (
            <DeliveryType
              pickUpStores={storeWithPickUp}
              quoteSimulationResponses={calculations}
              address={address}
            />
          ) : (
            <SkeletonSearchShipping />
          ))}
      </Flex>
    )
  }
)
